// features/pos/components/ShiftCloseModal.tsx
// REDESIGNED UI/UX - Same Logic, Better Experience

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';
import {
    X, DoorOpen, Banknote, CreditCard, Building2, Smartphone,
    Calculator, AlertTriangle, CheckCircle, Loader2,
    Wallet, TrendingUp, TrendingDown, Minus, Plus,
    ChevronRight, ChevronLeft, FileText, Clock
} from 'lucide-react';
import { clsx } from 'clsx';
import apiClient from '@/lib/api-client';

interface ShiftCloseModalProps {
    isOpen: boolean;
    onClose: () => void;
    shift: any;
    onShiftClosed: () => void;
}

const QUICK_ADJUSTMENTS = [
    { label: 'Match', adjustment: 0 },
    { label: '+₦500', adjustment: 500 },
    { label: '+₦1K', adjustment: 1000 },
    { label: '+₦2K', adjustment: 2000 },
    { label: '+₦5K', adjustment: 5000 },
    { label: '-₦500', adjustment: -500 },
];

export function ShiftCloseModal({ isOpen, onClose, shift, onShiftClosed }: ShiftCloseModalProps) {
    const tenantId = useAuthStore(s => s.user?.tenantId) || '';

    const [step, setStep] = useState<'review' | 'count' | 'confirm' | 'closing'>('review');
    const [countedCash, setCountedCash] = useState('');
    const [notes, setNotes] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [error, setError] = useState('');
    const [showQuickAdjust, setShowQuickAdjust] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            setStep('review');
            setCountedCash('');
            setNotes('');
            setError('');
            setIsClosing(false);
            setShowQuickAdjust(false);
        }
    }, [isOpen]);

    // Focus input when on count step
    useEffect(() => {
        if (step === 'count' && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [step]);

    const expectedCash = parseFloat(shift?.expectedCash || '0');
    const totalSales = parseFloat(shift?.totalSales || '0');
    const totalCashSales = parseFloat(shift?.totalCashSales || '0');
    const totalCardSales = parseFloat(shift?.totalCardSales || '0');
    const totalTransferSales = parseFloat(shift?.totalTransferSales || '0');
    const totalMomoSales = parseFloat(shift?.totalMomoSales || '0');
    const transactionCount = shift?.transactionCount || 0;
    const voidCount = shift?.voidCount || 0;
    const openingCash = parseFloat(shift?.openingCash || '0');

    const counted = parseFloat(countedCash) || 0;
    const variance = counted - expectedCash;
    const hasVariance = Math.abs(variance) > 0.99;
    const variancePercent = expectedCash > 0 ? (variance / expectedCash) * 100 : 0;

    const formatNaira = (val: number) => {
        if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
        return `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const handleCloseShift = async () => {
        if (!shift?.id) return;

        setIsClosing(true);
        setError('');

        try {
            await apiClient.post(`/tenants/${tenantId}/shifts/${shift.id}/close`, {
                countedCash: countedCash || '0',
                notes: notes || undefined,
            });

            setStep('closing');
            setTimeout(() => {
                onShiftClosed();
                onClose();
            }, 2000);
        } catch (err: any) {
            setError(err?.response?.data?.error?.message || 'Failed to close shift');
            setIsClosing(false);
        }
    };

    const handleQuickAdjust = (adjustment: number) => {
        if (adjustment === 0) {
            setCountedCash(expectedCash.toFixed(2));
        } else {
            const newAmount = Math.max(0, (counted || expectedCash) + adjustment);
            setCountedCash(newAmount.toFixed(2));
        }
        setShowQuickAdjust(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && step === 'confirm') {
            handleCloseShift();
        }
        if (e.key === 'Escape') {
            if (step === 'count') setStep('review');
            else if (step === 'confirm') setStep('count');
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onKeyDown={handleKeyDown}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-slate-100 flex-shrink-0 bg-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 shadow-md">
                                        <DoorOpen size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">
                                            {step === 'review' && 'Close Shift'}
                                            {step === 'count' && 'Count Cash'}
                                            {step === 'confirm' && 'Confirm Close'}
                                            {step === 'closing' && 'Closing...'}
                                        </h2>
                                        <p className="text-xs text-slate-500">
                                            {step === 'review' && 'Reconcile & close your shift'}
                                            {step === 'count' && 'Enter the physical cash amount'}
                                            {step === 'confirm' && 'Verify before closing'}
                                            {step === 'closing' && 'Finalizing transactions'}
                                        </p>
                                    </div>
                                </div>
                                {step !== 'closing' && (
                                    <button
                                        onClick={onClose}
                                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                                        type="button"
                                    >
                                        <X size={18} className="text-slate-400" />
                                    </button>
                                )}
                            </div>

                            {/* Step Indicator */}
                            {step !== 'closing' && (
                                <div className="flex items-center gap-2 mt-4">
                                    {['review', 'count', 'confirm'].map((s, idx) => {
                                        const stepIndex = ['review', 'count', 'confirm'].indexOf(step);
                                        const isActive = idx === stepIndex;
                                        const isComplete = idx < stepIndex;

                                        return (
                                            <div key={s} className="flex items-center gap-2 flex-1">
                                                <div className={clsx(
                                                    'h-1.5 rounded-full flex-1 transition-all',
                                                    isActive ? 'bg-orange-500' : isComplete ? 'bg-emerald-500' : 'bg-slate-200'
                                                )} />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {step === 'review' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-4"
                                >
                                    {/* Expected Cash Card */}
                                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 text-white shadow-lg">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm text-slate-300">Expected Cash</span>
                                            <span className="text-2xl font-black text-emerald-400">{formatNaira(expectedCash)}</span>
                                        </div>
                                        <div className="border-t border-slate-700 pt-3 space-y-1.5 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-300">Opening Cash</span>
                                                <span className="font-semibold text-slate-200">{formatNaira(openingCash)}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-300">+ Cash Sales</span>
                                                <span className="font-semibold text-emerald-400">+{formatNaira(totalCashSales)}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-300">Total Sales</span>
                                                <span className="font-semibold text-slate-200">{formatNaira(totalSales)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Methods */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                                            <div className="p-1.5 rounded-lg bg-emerald-200">
                                                <Banknote size={14} className="text-emerald-700" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] text-emerald-600 font-bold uppercase">Cash</p>
                                                <p className="text-sm font-bold text-emerald-700 truncate">{formatNaira(totalCashSales)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2.5 p-3 bg-purple-50 rounded-xl border border-purple-100">
                                            <div className="p-1.5 rounded-lg bg-purple-200">
                                                <CreditCard size={14} className="text-purple-700" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] text-purple-600 font-bold uppercase">Card</p>
                                                <p className="text-sm font-bold text-purple-700 truncate">{formatNaira(totalCardSales)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2.5 p-3 bg-blue-50 rounded-xl border border-blue-100">
                                            <div className="p-1.5 rounded-lg bg-blue-200">
                                                <Building2 size={14} className="text-blue-700" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] text-blue-600 font-bold uppercase">Transfer</p>
                                                <p className="text-sm font-bold text-blue-700 truncate">{formatNaira(totalTransferSales)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-100">
                                            <div className="p-1.5 rounded-lg bg-amber-200">
                                                <Smartphone size={14} className="text-amber-700" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] text-amber-600 font-bold uppercase">MoMo</p>
                                                <p className="text-sm font-bold text-amber-700 truncate">{formatNaira(totalMomoSales)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                                        <span className="flex items-center gap-1">
                                            <Clock size={12} />
                                            {transactionCount} sale{transactionCount !== 1 ? 's' : ''}
                                        </span>
                                        {voidCount > 0 && (
                                            <>
                                                <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                <span className="text-rose-500 font-semibold flex items-center gap-1">
                                                    <AlertTriangle size={12} />
                                                    {voidCount} void{voidCount !== 1 ? 's' : ''}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => setStep('count')}
                                        className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-500/25 hover:from-orange-600 hover:to-red-700 transition-all flex items-center justify-center gap-2"
                                        type="button"
                                    >
                                        Continue to Cash Count
                                        <ChevronRight size={16} />
                                    </motion.button>
                                </motion.div>
                            )}

                            {step === 'count' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-4"
                                >
                                    {/* Expected Amount */}
                                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-center">
                                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Expected Cash</p>
                                        <p className="text-3xl font-black text-slate-700">{formatNaira(expectedCash)}</p>
                                        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-slate-400">
                                            <span>Opening: {formatNaira(openingCash)}</span>
                                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                                            <span>+ Cash Sales: {formatNaira(totalCashSales)}</span>
                                        </div>
                                    </div>

                                    {/* Cash Input */}
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-2">
                                            <div className="p-1 rounded-lg bg-emerald-100">
                                                <Wallet size={12} className="text-emerald-600" />
                                            </div>
                                            Counted Cash Amount *
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-300">₦</span>
                                            <input
                                                ref={inputRef}
                                                type="number"
                                                value={countedCash}
                                                onChange={(e) => {
                                                    setCountedCash(e.target.value);
                                                    setError('');
                                                }}
                                                placeholder="0.00"
                                                className={clsx(
                                                    'w-full pl-10 pr-12 py-4 border-2 rounded-xl text-2xl font-bold text-center transition-all',
                                                    'bg-white placeholder:text-slate-300',
                                                    countedCash && parseFloat(countedCash) > 0
                                                        ? hasVariance
                                                            ? 'border-rose-300 focus:ring-4 focus:ring-rose-500/10 focus:border-rose-400'
                                                            : 'border-emerald-300 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-400'
                                                        : 'border-slate-200 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-400'
                                                )}
                                            />
                                            <button
                                                onClick={() => setShowQuickAdjust(!showQuickAdjust)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
                                                type="button"
                                                title="Quick adjustments"
                                            >
                                                {showQuickAdjust ? <Minus size={18} /> : <Plus size={18} />}
                                            </button>
                                        </div>

                                        {/* Quick Adjustments */}
                                        <AnimatePresence>
                                            {showQuickAdjust && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="grid grid-cols-3 gap-2 mt-2">
                                                        {QUICK_ADJUSTMENTS.map(({ label, adjustment }) => (
                                                            <motion.button
                                                                key={label}
                                                                whileHover={{ scale: 1.03 }}
                                                                whileTap={{ scale: 0.97 }}
                                                                onClick={() => handleQuickAdjust(adjustment)}
                                                                className={clsx(
                                                                    'px-2 py-2 rounded-lg text-xs font-bold transition-all border-2',
                                                                    adjustment > 0
                                                                        ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300'
                                                                        : adjustment < 0
                                                                            ? 'border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300'
                                                                            : 'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                                                                )}
                                                                type="button"
                                                            >
                                                                {label}
                                                            </motion.button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Variance Display */}
                                    <AnimatePresence>
                                        {countedCash && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                className={clsx(
                                                    'rounded-xl p-4 text-center border-2',
                                                    !hasVariance
                                                        ? 'bg-emerald-50 border-emerald-200'
                                                        : 'bg-rose-50 border-rose-200'
                                                )}
                                            >
                                                <div className="flex items-center justify-center gap-2 mb-2">
                                                    {!hasVariance ? (
                                                        <CheckCircle size={20} className="text-emerald-500" />
                                                    ) : (
                                                        <AlertTriangle size={20} className="text-rose-500" />
                                                    )}
                                                    <span className={clsx(
                                                        'text-base font-bold',
                                                        !hasVariance ? 'text-emerald-700' : 'text-rose-700'
                                                    )}>
                                                        {!hasVariance ? 'Balanced!' : variance > 0 ? 'Overage' : 'Shortage'}
                                                    </span>
                                                </div>

                                                {hasVariance && (
                                                    <>
                                                        <p className={clsx(
                                                            'text-3xl font-black',
                                                            variance > 0 ? 'text-emerald-600' : 'text-rose-600'
                                                        )}>
                                                            {variance > 0 ? '+' : ''}{formatNaira(Math.abs(variance))}
                                                        </p>
                                                        <div className="mt-1 flex items-center justify-center gap-1 text-xs text-slate-500">
                                                            {variance > 0 ? (
                                                                <TrendingUp size={12} className="text-emerald-500" />
                                                            ) : (
                                                                <TrendingDown size={12} className="text-rose-500" />
                                                            )}
                                                            <span>
                                                                {Math.abs(variancePercent).toFixed(1)}%
                                                                {variance > 0 ? ' above' : ' below'} expected
                                                            </span>
                                                        </div>
                                                    </>
                                                )}

                                                {!hasVariance && (
                                                    <p className="text-sm text-emerald-600 font-medium mt-1">
                                                        Cash count matches expected amount
                                                    </p>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* Notes for Variance */}
                                    {hasVariance && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <label className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-2">
                                                <div className="p-1 rounded-lg bg-amber-100">
                                                    <FileText size={12} className="text-amber-600" />
                                                </div>
                                                Variance Explanation *
                                            </label>
                                            <textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Explain the reason for the variance..."
                                                rows={2}
                                                className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 resize-none transition-all"
                                            />
                                        </motion.div>
                                    )}

                                    {/* Navigation */}
                                    <div className="flex gap-3 pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep('review')}
                                            className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-1"
                                            type="button"
                                        >
                                            <ChevronLeft size={16} />
                                            Back
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep('confirm')}
                                            disabled={!countedCash || (hasVariance && !notes)}
                                            className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-500/25 hover:from-orange-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                            type="button"
                                        >
                                            Review & Confirm
                                            <ChevronRight size={16} />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}

                            {step === 'confirm' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="text-center py-2">
                                        <motion.div
                                            animate={{ scale: [1, 1.05, 1] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                            className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3"
                                        >
                                            <AlertTriangle size={28} className="text-amber-600" />
                                        </motion.div>
                                        <h3 className="text-lg font-bold text-slate-900">Confirm Shift Close</h3>
                                        <p className="text-xs text-slate-500 mt-1">This action cannot be undone</p>
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 border border-slate-200">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Expected Cash</span>
                                            <span className="font-bold text-slate-700">{formatNaira(expectedCash)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Counted Cash</span>
                                            <span className="font-bold text-slate-700">{formatNaira(counted)}</span>
                                        </div>
                                        <div className="border-t border-slate-200 pt-2.5">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-bold text-slate-700">Variance</span>
                                                <span className={clsx(
                                                    'text-lg font-black',
                                                    !hasVariance ? 'text-emerald-600' :
                                                        variance > 0 ? 'text-emerald-600' : 'text-rose-600'
                                                )}>
                                                    {!hasVariance ? '₦0.00' :
                                                        `${variance > 0 ? '+' : ''}${formatNaira(variance)}`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    {notes && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                            <p className="text-xs font-bold text-amber-700 mb-0.5">Variance Note:</p>
                                            <p className="text-sm text-amber-800">{notes}</p>
                                        </div>
                                    )}

                                    {/* Error */}
                                    <AnimatePresence>
                                        {error && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700"
                                            >
                                                <AlertTriangle size={16} className="flex-shrink-0" />
                                                {error}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep('count')}
                                            className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-1"
                                            type="button"
                                        >
                                            <ChevronLeft size={16} />
                                            Back
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleCloseShift}
                                            disabled={isClosing}
                                            className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-500/25 hover:from-orange-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                            type="button"
                                        >
                                            {isClosing ? (
                                                <>
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                        className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                                                    />
                                                    Closing...
                                                </>
                                            ) : (
                                                <>
                                                    <DoorOpen size={16} />
                                                    Close Shift
                                                </>
                                            )}
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}

                            {step === 'closing' && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex flex-col items-center justify-center py-12"
                                >
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                                        className="mb-5"
                                    >
                                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                                            <Loader2 size={36} className="text-emerald-500" />
                                        </div>
                                    </motion.div>

                                    <p className="text-lg font-bold text-slate-700">Closing Shift...</p>
                                    <p className="text-sm text-slate-500 mt-1">Finalizing all transactions</p>

                                    <div className="mt-6 space-y-2 text-sm text-slate-400">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle size={14} className="text-emerald-400" />
                                            Reconciling payments
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle size={14} className="text-emerald-400" />
                                            Updating inventory
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle size={14} className="text-emerald-400" />
                                            Generating shift report
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}